import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Linkedin, Facebook, RefreshCw, Power, Trash2, ImageIcon, Hash, Pin, Film, Instagram, Clapperboard, Eye, Globe, Megaphone } from "lucide-react";
import {
  socialStatus, socialToggle, socialConnectLinkedIn, socialConnectFacebook,
  socialSelectFacebookPage, socialBackfill, socialDisconnect, socialClicks,
  socialPinterestSaveApp, socialPinterestOAuthUrl, socialPinterestBoards, socialPinterestSelect,
  socialInstagramDetect, socialTikTokSaveApp, socialTikTokOAuthUrl,
  socialFacebookPosts, socialFacebookDeletePost, socialFacebookDeleteAveno, socialFacebookFixLinks,
  socialFacebookRequeueFailed, socialNetworksList, socialNetworkSave, socialNetworkDelete,
  socialPreview, listPublicBlog, socialSetVoice, socialVoiceSample, kaiAnnounce,
} from "@/lib/api";

const chip = (status) => {
  const map = {
    ok: "bg-green-100 text-green-800",
    done: "bg-green-100 text-green-800",
    connected: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
    needs_reauth: "bg-red-100 text-red-800",
    not_connected: "bg-secondary text-muted-foreground",
  };
  return `px-2 py-0.5 text-xs font-mono uppercase tracking-wide ${map[status] || "bg-secondary text-muted-foreground"}`;
};

const ExtraNetworks = () => {
  const [nets, setNets] = useState([]);
  const [open, setOpen] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState("");
  const load = useCallback(() => socialNetworksList().then((d) => setNets(d.networks || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async (key) => {
    setSaving(key);
    try {
      const r = await socialNetworkSave(key, vals[key] || {});
      toast.success(r.connected ? "Connected — every new post flows here automatically" : "Saved, but some fields are still missing");
      setVals((p) => ({ ...p, [key]: {} }));
      setOpen(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(""); }
  };
  return (
    <div className="border border-border p-6 bg-card" data-testid="extra-networks">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-semibold">More networks — global reach</h4>
        <a href="/exports/SOCIAL_NETWORKS_SETUP.md" target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid="extra-networks-guide">Setup guide</a>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Connect once and every new post, meme and reel flows to that network automatically — paced per network, with UTM click
        tracking. Article platforms (Dev.to, Hashnode, Blogger, WordPress) receive the full article with a canonical link back to
        juliusmason.com, which also helps SEO. YouTube receives your daily reels as Shorts.
      </p>
      <div className="space-y-2">
        {nets.map((n) => (
          <div key={n.key} className="border border-border" data-testid={`extra-net-${n.key}`}>
            <button type="button" onClick={() => setOpen(open === n.key ? null : n.key)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition">
              <span>
                <span className="font-medium text-sm">{n.label}</span>
                <span className="block text-xs text-muted-foreground">{n.regions} · {n.content}</span>
              </span>
              <span className={`text-xs font-semibold flex-shrink-0 ${n.connected ? "text-green-700" : "text-muted-foreground"}`} data-testid={`extra-net-status-${n.key}`}>
                {n.connected ? "Connected" : "Connect"}
              </span>
            </button>
            {open === n.key && (
              <div className="px-4 pb-4 pt-2 border-t border-border grid gap-2">
                {n.fields.map((f) => (
                  <input key={f.key} type={f.secret ? "password" : "text"} placeholder={f.label + (f.placeholder ? ` — ${f.placeholder}` : "")}
                    value={(vals[n.key] || {})[f.key] || ""} autoComplete="new-password"
                    onChange={(e) => setVals((p) => ({ ...p, [n.key]: { ...(p[n.key] || {}), [f.key]: e.target.value } }))}
                    data-testid={`extra-net-${n.key}-${f.key}`}
                    className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
                ))}
                <div className="flex items-center gap-3 mt-1">
                  <button onClick={() => save(n.key)} disabled={saving === n.key} data-testid={`extra-net-save-${n.key}`}
                    className="bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition">
                    {saving === n.key ? "Saving…" : n.connected ? "Update" : "Connect"}
                  </button>
                  {n.connected && (
                    <button onClick={async () => { await socialNetworkDelete(n.key); toast.success(`${n.label} disconnected`); await load(); }}
                      data-testid={`extra-net-remove-${n.key}`} className="text-xs text-red-600 underline">Disconnect</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminAutoPost() {
  const [st, setSt] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [liToken, setLiToken] = useState("");
  const [fb, setFb] = useState({ app_id: "", app_secret: "", user_token: "" });
  const [fbPages, setFbPages] = useState(null);
  const [fbPageId, setFbPageId] = useState("");
  const [busy, setBusy] = useState("");
  const [perDay, setPerDay] = useState(4);
  const [pinApp, setPinApp] = useState({ app_id: "", app_secret: "" });
  const [pinBoards, setPinBoards] = useState(null);
  const [pinBoardId, setPinBoardId] = useState("");
  const [ttApp, setTtApp] = useState({ client_key: "", client_secret: "" });
  const [fbPosts, setFbPosts] = useState(null);
  const [previewPosts, setPreviewPosts] = useState([]);
  const [previewSlug, setPreviewSlug] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [voiceSel, setVoiceSel] = useState("am_michael");
  const [kaiResult, setKaiResult] = useState(null);

  const load = useCallback(() => {
    socialStatus().then((d) => { setSt(d); setVoiceSel(d.voice || "am_michael"); }).catch(() => toast.error("Could not load social status"));
    socialClicks(30).then(setClicks).catch(() => setClicks(null));
    listPublicBlog(100).then((rows) => setPreviewPosts([...rows].sort((a, b) => (b.category === "AVENO") - (a.category === "AVENO")))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  if (!st) return <p className="text-muted-foreground mt-10">Loading auto-posting…</p>;

  return (
    <div data-testid="autopost-panel" className="mt-12 max-w-3xl">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-2xl">Auto-Post to LinkedIn &amp; Facebook</h3>
        <div className="flex items-center gap-2">
          <button
            data-testid="fb-requeue-failed-btn"
            disabled={busy === "fbretry"}
            title="Re-opens Facebook for posts that failed during the app outage — networks that already succeeded are left untouched"
            onClick={async () => {
              setBusy("fbretry");
              try {
                const r = await socialFacebookRequeueFailed();
                toast.success(r.requeued ? `${r.requeued} post(s) will retry Facebook shortly` : "Nothing to retry — no outage-failed Facebook posts");
                await load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
              finally { setBusy(""); }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-border text-muted-foreground hover:text-foreground transition"
          >
            <Facebook className="w-4 h-4" /> {busy === "fbretry" ? "Retrying…" : "Retry failed FB"}
          </button>
          <button
            data-testid="autopost-toggle-btn"
            onClick={() => run("toggle", () => socialToggle(!st.enabled), st.enabled ? "Auto-posting paused" : "Auto-posting enabled")}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${st.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}
          >
            <Power className="w-4 h-4" /> {st.enabled ? "On" : "Off"}
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Every new blog post (manual or Auto Blog) is shared automatically with the full article text, a link back to the post,
        an AI-generated illustration matched to the content, and trending + niche hashtags. LinkedIn captions are capped at
        3,000 characters by LinkedIn — longer articles end with a "Read the full article" link.
      </p>

      {/* Voiceover voice */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="social-voice-row">
        <label className="overline text-muted-foreground">Voiceover voice</label>
        <select data-testid="social-voice-select" value={voiceSel}
          onChange={(e) => { const v = e.target.value; setVoiceSel(v); run("voice", () => socialSetVoice(v), "Voice updated — applies to the next videos"); }}
          className="bg-secondary px-3 py-2 text-sm outline-none">
          <option value="am_michael">Michael — warm US male</option>
          <option value="am_adam">Adam — deep US male</option>
          <option value="am_eric">Eric — casual US male</option>
          <option value="af_heart">Heart — warm US female</option>
          <option value="af_sarah">Sarah — calm US female</option>
          <option value="af_sky">Sky — bright US female</option>
        </select>
        <button data-testid="social-voice-sample-btn" disabled={busy === "sample"}
          onClick={async () => {
            setBusy("sample");
            try {
              const blob = await socialVoiceSample(voiceSel);
              new Audio(URL.createObjectURL(blob)).play();
            } catch (e2) { toast.error(e2?.response?.data?.detail || "Sample failed — try again"); }
            finally { setBusy(""); }
          }}
          className="inline-flex items-center gap-2 bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/70 transition disabled:opacity-50">
          {busy === "sample" ? "Generating…" : "Hear it"}
        </button>
        <span className="text-xs text-muted-foreground">videos are now voiceover-only — no on-screen cameo</span>
      </div>

      {/* Queue + backfill */}
      <div className="border border-border p-5 bg-card mb-6 flex flex-wrap items-center gap-4">
        <div className="flex gap-4 text-sm">
          <span data-testid="queue-pending">Queued: <strong>{st.queue?.pending ?? 0}</strong></span>
          <span data-testid="queue-done">Posted: <strong>{st.queue?.done ?? 0}</strong></span>
          <span data-testid="queue-error">Failed: <strong>{st.queue?.error ?? 0}</strong></span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="backfill-pace-select" value={perDay} onChange={(e) => setPerDay(Number(e.target.value))}
            className="bg-secondary px-2 py-2 text-sm outline-none" title="Posts per day">
            {[2, 4, 7, 10].map((n) => <option key={n} value={n}>{n}/day</option>)}
          </select>
          <button
            data-testid="backfill-btn"
            disabled={busy === "backfill"}
            onClick={() => run("backfill", () => socialBackfill(perDay), `Existing posts queued — ${perDay} per day`)}
            className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${busy === "backfill" ? "animate-spin" : ""}`} /> Share all existing posts
          </button>
        </div>
      </div>

      {/* Clicks from social */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="social-clicks-card">
        <h4 className="font-semibold mb-1">Clicks from social — last 30 days</h4>
        <p className="text-xs text-muted-foreground mb-4">Every shared link carries UTM tags, so you can see which platform actually sends readers.</p>
        {clicks ? (
          <>
            <div className="flex gap-8 mb-4">
              <div data-testid="clicks-linkedin" className="flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                <span className="font-display text-3xl">{clicks.by_network?.linkedin || 0}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">LinkedIn clicks</span>
              </div>
              <div data-testid="clicks-facebook" className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-[#1877F2]" />
                <span className="font-display text-3xl">{clicks.by_network?.facebook || 0}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Facebook clicks</span>
              </div>
              <div data-testid="clicks-pinterest" className="flex items-center gap-2">
                <Pin className="w-4 h-4 text-[#E60023]" />
                <span className="font-display text-3xl">{clicks.by_network?.pinterest || 0}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Pinterest clicks</span>
              </div>
              <div data-testid="clicks-instagram" className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-[#E1306C]" />
                <span className="font-display text-3xl">{clicks.by_network?.instagram || 0}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Instagram clicks</span>
              </div>
              <div data-testid="clicks-tiktok" className="flex items-center gap-2">
                <Clapperboard className="w-4 h-4" />
                <span className="font-display text-3xl">{clicks.by_network?.tiktok || 0}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">TikTok clicks</span>
              </div>
            </div>
            {(clicks.top_posts || []).length > 0 && (
              <div className="text-sm" data-testid="clicks-top-posts">
                <p className="overline text-muted-foreground mb-2">Top posts</p>
                {clicks.top_posts.map((t, i) => (
                  <div key={`${t.slug}-${t.source}`} data-testid={`clicks-post-${i}`} className="flex justify-between gap-3 py-1 border-b border-border last:border-0">
                    <span className="truncate">{t.slug}</span>
                    <span className="text-muted-foreground whitespace-nowrap">{t.source} · {t.clicks} clicks</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No clicks tracked yet — they appear here once shared posts start bringing readers.</p>
        )}
      </div>

      {/* LinkedIn */}
      <div className="border border-border p-6 bg-card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Linkedin className="w-5 h-5 text-[#0A66C2]" />
          <h4 className="font-semibold">LinkedIn — personal profile</h4>
          {st.linkedin && <span data-testid="linkedin-status" className={chip(st.linkedin.status || "connected")}>{st.linkedin.status || "connected"}</span>}
        </div>
        {st.linkedin ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Posting as <strong className="text-foreground">{st.linkedin.name}</strong> · connected {new Date(st.linkedin.connected_at).toLocaleDateString()}
              {st.linkedin.status === "needs_reauth" && <span className="text-red-600 block">Token expired or rejected — paste a fresh token below by disconnecting first.</span>}
            </p>
            <button data-testid="linkedin-disconnect-btn" onClick={() => run("li-disc", () => socialDisconnect("linkedin"), "LinkedIn disconnected")}
              className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 px-3 py-2 hover:bg-red-50 transition">
              <Trash2 className="w-4 h-4" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Paste a LinkedIn access token with the <code className="text-xs">openid</code>, <code className="text-xs">profile</code> and <code className="text-xs">w_member_social</code> scopes — see <a href="/exports/SOCIAL_AUTOPOST_SETUP.md" target="_blank" rel="noreferrer" className="text-primary underline">the setup guide</a>. Tokens last ~60 days.</p>
            <textarea data-testid="linkedin-token-input" value={liToken} onChange={(e) => setLiToken(e.target.value)} rows={2}
              placeholder="Paste LinkedIn access token…" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary font-mono" />
            <button data-testid="linkedin-connect-btn" disabled={busy === "li" || liToken.trim().length < 20}
              onClick={() => run("li", () => socialConnectLinkedIn(liToken.trim()), "LinkedIn connected")}
              className="bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              Connect LinkedIn
            </button>
          </div>
        )}
      </div>

      {/* Facebook */}
      <div className="border border-border p-6 bg-card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Facebook className="w-5 h-5 text-[#1877F2]" />
          <h4 className="font-semibold">Facebook — business Page</h4>
          {st.facebook && <span data-testid="fb-status" className={chip(st.facebook.status || "connected")}>{st.facebook.status || "connected"}</span>}
        </div>
        {st.facebook ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Posting to Page <strong className="text-foreground">{st.facebook.page_name}</strong> · connected {new Date(st.facebook.connected_at).toLocaleDateString()}
            </p>
            <button data-testid="fb-disconnect-btn" onClick={() => run("fb-disc", () => socialDisconnect("facebook"), "Facebook disconnected")}
              className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 px-3 py-2 hover:bg-red-50 transition">
              <Trash2 className="w-4 h-4" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Create a Meta app, grab a user token with <code className="text-xs">pages_show_list</code> + <code className="text-xs">pages_manage_posts</code> + <code className="text-xs">pages_manage_engagement</code> (needed to delete old posts), then paste the three values below — <a href="/exports/SOCIAL_AUTOPOST_SETUP.md" target="_blank" rel="noreferrer" className="text-primary underline">setup guide</a>.</p>
            <input data-testid="fb-app-id-input" value={fb.app_id} onChange={(e) => setFb({ ...fb, app_id: e.target.value })} placeholder="Meta App ID" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <input data-testid="fb-app-secret-input" value={fb.app_secret} onChange={(e) => setFb({ ...fb, app_secret: e.target.value })} placeholder="Meta App Secret" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <textarea data-testid="fb-user-token-input" value={fb.user_token} onChange={(e) => setFb({ ...fb, user_token: e.target.value })} rows={2} placeholder="User access token (Graph API Explorer)…" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary font-mono" />
            <button data-testid="fb-connect-btn" disabled={busy === "fb" || !fb.app_id || !fb.app_secret || fb.user_token.trim().length < 20}
              onClick={() => run("fb", async () => { const r = await socialConnectFacebook({ app_id: fb.app_id.trim(), app_secret: fb.app_secret.trim(), user_token: fb.user_token.trim() }); setFbPages(r.pages); if (r.pages?.length === 1) setFbPageId(r.pages[0].id); }, "Pages loaded — pick yours below")}
              className="bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              Connect Facebook
            </button>
            {fbPages && (
              <div className="flex items-center gap-3 pt-2">
                <select data-testid="fb-page-select" value={fbPageId} onChange={(e) => setFbPageId(e.target.value)} className="bg-secondary px-3 py-2.5 text-sm outline-none flex-1">
                  <option value="">Choose your Page…</option>
                  {fbPages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button data-testid="fb-select-save-btn" disabled={!fbPageId || busy === "fb-sel"}
                  onClick={() => run("fb-sel", async () => { await socialSelectFacebookPage(fbPageId); setFbPages(null); }, "Facebook Page connected")}
                  className="bg-primary text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
                  Save Page
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Facebook post repair */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="fb-repair-card">
          <div className="flex items-center gap-2 mb-2">
            <Facebook className="w-5 h-5 text-[#1877F2]" />
            <h4 className="font-semibold">Published Facebook posts — repair</h4>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Posts published with the old format (AI artwork instead of the real product) can be deleted here — replacements with the actual
            product photo go out automatically from the refreshed queue. Posts you archived inside the Facebook app are invisible to the API:
            unarchive them there first if you want them listed, or just leave them (archived posts are already hidden from your Page).
            Deleting needs the <code className="text-xs">pages_manage_engagement</code> permission — if a delete is refused, reconnect
            Facebook above with a token that includes it.
          </p>
          {!st.facebook ? (
            <p className="text-sm text-muted-foreground" data-testid="fb-repair-connect-first">Connect Facebook above first to see and manage your Page's published posts.</p>
          ) : !fbPosts ? (
            <button data-testid="fb-posts-load-btn" disabled={busy === "fbposts"}
              onClick={() => run("fbposts", async () => { const r = await socialFacebookPosts(); setFbPosts(r.posts || []); }, "Page posts loaded")}
              className="bg-secondary px-4 py-2.5 text-sm font-semibold hover:bg-secondary/70 transition disabled:opacity-50">
              Load my Page posts
            </button>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <p className="text-sm text-muted-foreground" data-testid="fb-posts-count">
                  {fbPosts.length} posts · <strong className="text-foreground">{fbPosts.filter((p) => p.kind === "aveno" && !p.new_spec).length}</strong> old-format AVENO posts
                </p>
                <div className="flex gap-2">
                  <button data-testid="fb-posts-reload-btn" disabled={busy === "fbposts"}
                    onClick={() => run("fbposts", async () => { const r = await socialFacebookPosts(); setFbPosts(r.posts || []); }, "Reloaded")}
                    className="inline-flex items-center gap-1.5 bg-secondary px-3 py-2 text-xs font-semibold hover:bg-secondary/70 transition disabled:opacity-50">
                    <RefreshCw className={`w-3 h-3 ${busy === "fbposts" ? "animate-spin" : ""}`} /> Reload
                  </button>
                  <button data-testid="fb-fix-links-btn" disabled={busy === "fbfix"}
                    onClick={() => run("fbfix", async () => { const r = await socialFacebookFixLinks(); toast.success(`Links added to ${r.fixed} posts (${r.skipped} already had one)`); const l = await socialFacebookPosts(); setFbPosts(l.posts || []); }, "Links fixed")}
                    className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50">
                    Fix missing purchase links
                  </button>
                  <button data-testid="fb-delete-aveno-btn" disabled={busy === "fbdelall" || !fbPosts.some((p) => p.kind === "aveno" && !p.new_spec)}
                    onClick={() => {
                      const ids = fbPosts.filter((p) => p.kind === "aveno" && !p.new_spec).map((p) => p.id);
                      if (window.confirm(`Delete ${ids.length} OLD-FORMAT AVENO posts from your Facebook Page?\n\nNew-spec posts (real product video + store link) are kept. This cannot be undone.`))
                        run("fbdelall", async () => { const r = await socialFacebookDeleteAveno(ids); if (r.failed?.length) toast.error(`${r.failed.length} deletions refused — token may need pages_manage_engagement`); const l = await socialFacebookPosts(); setFbPosts(l.posts || []); }, "Old AVENO posts deleted");
                    }}
                    className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 text-xs font-semibold hover:bg-red-700 transition disabled:opacity-50">
                    <Trash2 className="w-3 h-3" /> Delete old-format AVENO posts
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {fbPosts.map((p, i) => (
                  <div key={p.id} data-testid={`fb-post-${i}`} className="flex items-center gap-3 text-sm border-b border-border pb-2 last:border-0">
                    {p.thumb ? <img src={p.thumb} alt="" className="w-10 h-10 object-cover flex-shrink-0" /> : <div className="w-10 h-10 bg-secondary flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{p.message || "(no text)"}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.created_time ? new Date(p.created_time).toLocaleDateString() : ""}
                        {p.kind === "aveno" && <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-mono uppercase">AVENO</span>}
                        {p.kind === "aveno" && p.new_spec && <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] font-mono uppercase">new spec</span>}
                        {p.kind === "blog" && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-mono uppercase">blog</span>}
                        {p.slug && <span className="ml-2 font-mono text-[10px]">{p.slug}</span>}
                      </p>
                    </div>
                    {p.permalink_url && <a href={p.permalink_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline flex-shrink-0">view</a>}
                    <button data-testid={`fb-post-delete-${i}`} disabled={busy === `fbdel-${p.id}`}
                      onClick={() => { if (window.confirm("Delete this Facebook post permanently?")) run(`fbdel-${p.id}`, async () => { await socialFacebookDeletePost(p.id, p.slug); setFbPosts(fbPosts.filter((x) => x.id !== p.id)); }, "Post deleted"); }}
                      className="text-red-600 border border-red-200 px-2 py-1.5 text-xs hover:bg-red-50 transition disabled:opacity-50 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

      {/* Post preview */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="social-preview-card">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-5 h-5 text-primary" />
          <h4 className="font-semibold">Post preview — exactly what each network gets</h4>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Pick any post to see the precise caption, purchase link and media each platform will publish. Read-only — nothing is generated or posted.
        </p>
        <select data-testid="social-preview-select" value={previewSlug}
          onChange={async (e) => {
            const slug = e.target.value;
            setPreviewSlug(slug);
            setPreview(null);
            if (!slug) return;
            setPreviewLoading(true);
            try { setPreview(await socialPreview(slug)); }
            catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); }
            finally { setPreviewLoading(false); }
          }}
          className="w-full bg-secondary px-3 py-2.5 text-sm outline-none mb-4">
          <option value="">Choose a post…</option>
          {previewPosts.map((p) => <option key={p.slug} value={p.slug}>{p.category === "AVENO" ? "[AVENO] " : ""}{p.title}</option>)}
        </select>
        {previewLoading && <p className="text-sm text-muted-foreground">Composing preview…</p>}
        {preview && (
          <>
            {preview.media_pending && (
              <p className="text-xs text-amber-800 bg-amber-50 px-3 py-2 mb-3" data-testid="social-preview-pending-note">
                Not shared yet — hashtags and (for articles) the illustration are generated at publish time. AVENO posts always attach the real product photo and video shown here.
              </p>
            )}
            <div className="flex items-center gap-4 mb-4">
              {preview.image && <img src={preview.image} alt="" data-testid="social-preview-image" className="w-16 h-16 object-cover border border-border" />}
              {preview.video
                ? <span className="inline-flex items-center gap-1.5 text-xs text-green-700" data-testid="social-preview-video"><Film className="w-4 h-4" /> narrated product video attached</span>
                : preview.kind === "aveno" && <span className="text-xs text-red-600" data-testid="social-preview-novideo">product video not generated yet — the post won't publish without it</span>}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(preview.networks).map(([net, n]) => (
                <div key={net} data-testid={`social-preview-${net}`} className="border border-border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {net === "facebook" ? <Facebook className="w-4 h-4 text-[#1877F2]" />
                      : net === "instagram" ? <Instagram className="w-4 h-4 text-[#E1306C]" />
                      : net === "tiktok" ? <Clapperboard className="w-4 h-4" />
                      : net === "linkedin" ? <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                      : <Pin className="w-4 h-4 text-[#E60023]" />}
                    <span className="font-semibold capitalize text-sm">{net}</span>
                    {n.media && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-secondary text-muted-foreground">{n.media}</span>}
                  </div>
                  <p className="text-xs whitespace-pre-wrap break-words mb-2">{n.caption}</p>
                  {n.link && <a href={n.link} target="_blank" rel="noreferrer" className="text-primary text-xs underline break-all">{n.link}</a>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pinterest */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="pinterest-card">
        <div className="flex items-center gap-2 mb-4">
          <Pin className="w-5 h-5 text-[#E60023]" />
          <h4 className="font-semibold">Pinterest — business account</h4>
          {st.pinterest && <span data-testid="pin-status" className={chip(st.pinterest.status || "connected")}>{st.pinterest.status || "connected"}</span>}
        </div>
        {st.pinterest ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Pinning as <strong className="text-foreground">{st.pinterest.username}</strong>
                {st.pinterest.board_name ? <> to board <strong className="text-foreground">{st.pinterest.board_name}</strong></> : " — now pick a board below"}
                {" "}· connected {new Date(st.pinterest.connected_at).toLocaleDateString()}
              </p>
              <button data-testid="pin-disconnect-btn" onClick={() => run("pin-disc", () => socialDisconnect("pinterest"), "Pinterest disconnected")}
                className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 px-3 py-2 hover:bg-red-50 transition">
                <Trash2 className="w-4 h-4" /> Disconnect
              </button>
            </div>
            {!st.pinterest.board_id && (
              <div className="flex flex-wrap items-center gap-3">
                {!pinBoards ? (
                  <button data-testid="pin-load-boards-btn" disabled={busy === "pin-boards"}
                    onClick={() => run("pin-boards", async () => { const r = await socialPinterestBoards(); setPinBoards(r.boards); }, "Boards loaded")}
                    className="bg-secondary px-4 py-2.5 text-sm font-semibold hover:bg-secondary/70 transition disabled:opacity-50">
                    Load my boards
                  </button>
                ) : (
                  <>
                    <select data-testid="pin-board-select" value={pinBoardId} onChange={(e) => setPinBoardId(e.target.value)} className="bg-secondary px-3 py-2.5 text-sm outline-none flex-1 max-w-xs">
                      <option value="">Choose a board…</option>
                      {pinBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    <button data-testid="pin-board-save-btn" disabled={!pinBoardId || busy === "pin-sel"}
                      onClick={() => run("pin-sel", async () => { const b = pinBoards.find((x) => x.id === pinBoardId); await socialPinterestSelect(pinBoardId, b?.name || ""); setPinBoards(null); }, "Board saved")}
                      className="bg-primary text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
                      Save Board
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Create a free app at <a href="https://developers.pinterest.com/apps/" target="_blank" rel="noreferrer" className="text-primary underline">developers.pinterest.com/apps</a>,
              register the redirect URL from the <a href="/exports/SOCIAL_AUTOPOST_SETUP.md" target="_blank" rel="noreferrer" className="text-primary underline">setup guide</a>,
              then paste the App ID and secret and connect — one click, and tokens refresh themselves.
            </p>
            <input data-testid="pin-app-id-input" value={pinApp.app_id} onChange={(e) => setPinApp({ ...pinApp, app_id: e.target.value })} placeholder="Pinterest App ID" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <input data-testid="pin-app-secret-input" value={pinApp.app_secret} onChange={(e) => setPinApp({ ...pinApp, app_secret: e.target.value })} placeholder="Pinterest App secret" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <button data-testid="pin-connect-btn" disabled={busy === "pin" || !pinApp.app_id.trim() || !pinApp.app_secret.trim()}
              onClick={() => run("pin", async () => {
                await socialPinterestSaveApp({ app_id: pinApp.app_id.trim(), app_secret: pinApp.app_secret.trim() });
                const { url } = await socialPinterestOAuthUrl();
                window.location.href = url;
              }, "Redirecting to Pinterest…")}
              className="bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              Save &amp; Connect with Pinterest
            </button>
          </div>
        )}
      </div>

      {/* Instagram */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="instagram-card">
        <div className="flex items-center gap-2 mb-4">
          <Instagram className="w-5 h-5 text-[#E1306C]" />
          <h4 className="font-semibold">Instagram — professional account</h4>
          {st.instagram && <span data-testid="ig-status" className={chip(st.instagram.status || "connected")}>{st.instagram.status || "connected"}</span>}
        </div>
        {st.instagram ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Posting as <strong className="text-foreground">@{st.instagram.username}</strong> · connected {new Date(st.instagram.connected_at).toLocaleDateString()}
            </p>
            <button data-testid="ig-disconnect-btn" onClick={() => run("ig-disc", () => socialDisconnect("instagram"), "Instagram disconnected")}
              className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 px-3 py-2 hover:bg-red-50 transition">
              <Trash2 className="w-4 h-4" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Runs through your Facebook Page's linked Instagram professional account. Your Meta token needs the
              <code className="text-xs"> instagram_basic</code> + <code className="text-xs">instagram_content_publish</code> permissions —
              regenerate it with those (see the <a href="/exports/SOCIAL_AUTOPOST_SETUP.md" target="_blank" rel="noreferrer" className="text-primary underline">setup guide</a>),
              reconnect Facebook, then detect.
            </p>
            <button data-testid="ig-detect-btn" disabled={!st.facebook || busy === "ig"}
              onClick={() => run("ig", socialInstagramDetect, "Instagram linked")}
              className="bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              Detect my Instagram account
            </button>
            {!st.facebook && <p className="text-xs text-muted-foreground">Connect Facebook above first.</p>}
          </div>
        )}
      </div>

      {/* TikTok */}
      <div className="border border-border p-6 bg-card mb-6" data-testid="tiktok-card">
        <div className="flex items-center gap-2 mb-4">
          <Clapperboard className="w-5 h-5" />
          <h4 className="font-semibold">TikTok</h4>
          {st.tiktok && <span data-testid="tt-status" className={chip(st.tiktok.status || "connected")}>{st.tiktok.status || "connected"}</span>}
        </div>
        {st.tiktok ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Posting as <strong className="text-foreground">{st.tiktok.username}</strong> · connected {new Date(st.tiktok.connected_at).toLocaleDateString()}
            </p>
            <button data-testid="tt-disconnect-btn" onClick={() => run("tt-disc", () => socialDisconnect("tiktok"), "TikTok disconnected")}
              className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 px-3 py-2 hover:bg-red-50 transition">
              <Trash2 className="w-4 h-4" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Create a free app at <a href="https://developers.tiktok.com/" target="_blank" rel="noreferrer" className="text-primary underline">developers.tiktok.com</a> with
              the <code className="text-xs">user.info.basic</code> + <code className="text-xs">video.publish</code> scopes and the redirect URL from the
              <a href="/exports/SOCIAL_AUTOPOST_SETUP.md" target="_blank" rel="noreferrer" className="text-primary underline"> setup guide</a>, then paste the Client key and secret.
              Note: until TikTok approves your app, videos post as <em>private</em> (visible only to you) — public posting unlocks after their quick review.
            </p>
            <input data-testid="tt-client-key-input" value={ttApp.client_key} onChange={(e) => setTtApp({ ...ttApp, client_key: e.target.value })} placeholder="TikTok Client key" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <input data-testid="tt-client-secret-input" value={ttApp.client_secret} onChange={(e) => setTtApp({ ...ttApp, client_secret: e.target.value })} placeholder="TikTok Client secret" className="w-full bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <button data-testid="tt-connect-btn" disabled={busy === "tt" || !ttApp.client_key.trim() || !ttApp.client_secret.trim()}
              onClick={() => run("tt", async () => {
                await socialTikTokSaveApp({ client_key: ttApp.client_key.trim(), client_secret: ttApp.client_secret.trim() });
                const { url } = await socialTikTokOAuthUrl();
                window.location.href = url;
              }, "Redirecting to TikTok…")}
              className="bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              Save &amp; Connect with TikTok
            </button>
          </div>
        )}
      </div>

      {/* KAI launch announcement */}
      <div className="border border-border p-6 bg-card mb-8" data-testid="kai-launch-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h4 className="font-semibold mb-1 flex items-center gap-2"><Megaphone className="w-4 h-4" /> KAI launch announcement</h4>
            <p className="text-xs text-muted-foreground max-w-lg">
              One click: publishes the KAI launch blog post (auto-shared by the standard pipeline) and queues a branded
              announcement with the KAI visual across 11 product networks. Idempotent — safe to click again.
            </p>
            {kaiResult && (
              <p className="text-xs mt-2 text-emerald-700" data-testid="kai-launch-result">
                Blog: {kaiResult.blog?.already_existed ? "already published" : `published → /blog/${kaiResult.blog?.slug}`} ·
                Announcement job: {kaiResult.announcement_job?.queued ? "queued on 11 networks" : "already queued today"}
              </p>
            )}
          </div>
          <button
            data-testid="kai-announce-btn"
            disabled={busy === "kai"}
            onClick={() => run("kai", async () => setKaiResult(await kaiAnnounce()), "KAI announcement queued")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700 transition disabled:opacity-50"
          >
            <Megaphone className="w-4 h-4" /> {busy === "kai" ? "Launching…" : "Announce KAI launch"}
          </button>
        </div>
      </div>

      {/* Recent activity */}
      <div className="border border-border p-6 bg-card">
        <h4 className="font-semibold mb-4">Recent shares</h4>
        {(!st.recent || st.recent.length === 0) && <p className="text-sm text-muted-foreground" data-testid="social-jobs-empty">Nothing shared yet — publish a post or run the backfill.</p>}
        <div className="space-y-3">
          {(st.recent || []).map((j, i) => (
            <div key={j.slug || i} data-testid={`social-job-${i}`} className="text-sm border-b border-border pb-3 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium truncate">{j.title || j.slug}</span>
                <span className={chip(j.status)}>{j.status}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-1.5 items-center text-xs text-muted-foreground">
                {(j.targets || ["linkedin", "facebook", "instagram", "tiktok", "pinterest"]).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1">
                    {t === "linkedin" ? <Linkedin className="w-3 h-3" /> : t === "facebook" ? <Facebook className="w-3 h-3" /> : t === "instagram" ? <Instagram className="w-3 h-3" /> : t === "tiktok" ? <Clapperboard className="w-3 h-3" /> : t === "pinterest" ? <Pin className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {["linkedin", "facebook", "instagram", "tiktok", "pinterest"].includes(t) ? null : <span>{t}</span>}
                    <span className={chip(j.results?.[t]?.status || "pending")}>{j.results?.[t]?.status || "queued"}</span>
                  </span>
                ))}
                {j.media?.image && <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> image</span>}
                {j.media?.video && <span className="inline-flex items-center gap-1" data-testid={`social-job-video-${i}`}><Film className="w-3 h-3" /> video+voice</span>}
                {j.media?.hashtags?.length > 0 && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" /> {j.media.hashtags.length} tags</span>}
                {j.results?.linkedin?.detail && <span className="text-red-600 truncate max-w-xs" title={j.results.linkedin.detail}>LI: {j.results.linkedin.detail}</span>}
                {j.results?.facebook?.detail && <span className="text-red-600 truncate max-w-xs" title={j.results.facebook.detail}>FB: {j.results.facebook.detail}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ExtraNetworks />
    </div>
  );
}
